/**
 * CheckoutChamp Integration - Main JavaScript
 * This file handles all custom functionality for the subscription and checkout system
 */

(function() {
  'use strict';

  // Configuration globale
  const CONFIG = {
    apiBaseUrl: 'https://api.data.retroha.com',
    subscriptionVariantId: 52738903671121,
    subscriptionProductId: 10561091993937,
    campaignId: 13,
    shipProfileId: 11,
    get locale() {
      return document.documentElement.lang || 'fr';
    },
    shopDomain: null
  };

  // Utility Functions
  const Utils = {
    /**
     * Get current URL parameters
     */
    getUrlParams() {
      return new URLSearchParams(window.location.search);
    },

    /**
     * Show loading overlay
     */
    showLoading() {
      let overlay = document.getElementById('custom-loading-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'custom-loading-overlay';
        overlay.className = 'loading-overlay';
        overlay.innerHTML = '<div class="loading-spinner"></div>';
        document.body.appendChild(overlay);
      }
      overlay.style.display = 'flex';
    },

    /**
     * Hide loading overlay
     */
    hideLoading() {
      const overlay = document.getElementById('custom-loading-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    },

    /**
     * Show error message
     */
    showError(message, containerId = null) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message';
      errorDiv.textContent = message;
      
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          container.insertBefore(errorDiv, container.firstChild);
          setTimeout(() => errorDiv.remove(), 5000);
        }
      } else {
        document.body.insertBefore(errorDiv, document.body.firstChild);
        setTimeout(() => errorDiv.remove(), 5000);
      }
    },

    /**
     * Show success message
     */
    showSuccess(message, containerId = null) {
      const successDiv = document.createElement('div');
      successDiv.className = 'success-message';
      successDiv.textContent = message;
      
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          container.insertBefore(successDiv, container.firstChild);
          setTimeout(() => successDiv.remove(), 5000);
        }
      } else {
        document.body.insertBefore(successDiv, document.body.firstChild);
        setTimeout(() => successDiv.remove(), 5000);
      }
    },

    /**
     * Generate random footprint/ID
     */
    generateFootprint() {
      return 'fp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
  };

  // Local Storage Manager for Customer Account
  const AccountStorage = {
    /**
     * Save customer data to localStorage
     */
    save(customerData) {
      try {
        localStorage.setItem('customer_account', JSON.stringify(customerData));
        localStorage.setItem('customer_logged_in', 'true');
      } catch (e) {
        console.error('Error saving customer data:', e);
      }
    },

    /**
     * Get customer data from localStorage
     */
    get() {
      try {
        const data = localStorage.getItem('customer_account');
        return data ? JSON.parse(data) : null;
      } catch (e) {
        console.error('Error retrieving customer data:', e);
        return null;
      }
    },

    /**
     * Check if customer is logged in
     */
    isLoggedIn() {
      return localStorage.getItem('customer_logged_in') === 'true';
    },

    /**
     * Clear customer data
     */
    clear() {
      localStorage.removeItem('customer_account');
      localStorage.removeItem('customer_logged_in');
    },

    /**
     * Check if customer has active subscription
     */
    hasActiveSubscription() {
      const data = this.get();
      if (!data || !data.purchase) return false;
      const status = data.purchase.status;
      return ['ACTIVE', 'TRIAL', 'RECYCLE_BILLING'].includes(status);
    }
  };

  // Subscription Price Manager (50% discount for subscribers)
  const PriceManager = {
    /**
     * Check if element is for subscription product
     */
    isSubscriptionProduct(element) {
      // Check if element or its parents contain subscription product data
      let current = element;
      while (current && current !== document.body) {
        // Check for variant ID in various data attributes
        const variantId = current.getAttribute('data-variant-id') || 
                         current.getAttribute('data-product-variant-id');
        if (variantId && variantId == CONFIG.subscriptionVariantId) {
          return true;
        }
        
        // Check for product ID
        const productId = current.getAttribute('data-product-id');
        if (productId && productId == CONFIG.subscriptionProductId) {
          return true;
        }
        
        current = current.parentElement;
      }
      return false;
    },

    /**
     * Apply 50% discount to product prices if customer is subscribed
     */
    applySubscriptionDiscounts() {
      if (!AccountStorage.hasActiveSubscription()) return;

      // Find all price elements on the page
      const priceSelectors = [
        '.price__regular .price-item--regular',
        '.price-item--regular',
        '.product-price',
        '.cart-item__price',
        '.product-card__price',
        '.card__price'
      ];

      priceSelectors.forEach(selector => {
        const priceElements = document.querySelectorAll(selector);
        priceElements.forEach(element => {
          if (element.hasAttribute('data-discount-applied')) return;
          
          // Skip if this is a subscription product
          if (this.isSubscriptionProduct(element)) return;

          const priceText = element.textContent.trim();
          const priceMatch = priceText.match(/[\d,]+[.,]\d{2}/);
          
          if (priceMatch) {
            const priceStr = priceMatch[0].replace(',', '.');
            const originalPrice = parseFloat(priceStr);
            const discountedPrice = (originalPrice / 2).toFixed(2);

            // Create price display with strikethrough original and discounted price
            const priceContainer = document.createElement('div');
            priceContainer.className = 'price-with-subscription';
            priceContainer.innerHTML = `
              <span class="price-original">${priceText}</span>
              <span class="price-discounted">${discountedPrice.replace('.', ',')} €</span>
              <span class="subscription-badge">-50%</span>
            `;

            element.innerHTML = '';
            element.appendChild(priceContainer);
            element.setAttribute('data-discount-applied', 'true');
          }
        });
      });
    },

    /**
     * Initialize price discount observer
     */
    init() {
      // Apply discounts on page load
      if (AccountStorage.hasActiveSubscription()) {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.applySubscriptionDiscounts());
        } else {
          this.applySubscriptionDiscounts();
        }

        // Observe DOM changes to apply discounts to dynamically loaded content
        // Wait for document.body to be available
        const startObserver = () => {
          if (!document.body) {
            // If body is not ready, wait a bit and try again
            setTimeout(startObserver, 100);
            return;
          }

          const observer = new MutationObserver(() => {
            this.applySubscriptionDiscounts();
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        };

        startObserver();
      }
    }
  };

  // Facebook Pixel Integration
  const FacebookPixel = {
    /**
     * Parse pixel string from URL
     */
    parsePixels(pixelString) {
      if (!pixelString) return [];
      
      // Format: "pixelId:accessToken;pixelId2:accessToken2"
      const pixels = pixelString.split(';');
      return pixels.map(pixelPair => {
        const [pixelId, accessToken] = pixelPair.split(':');
        return { pixelId, accessToken };
      }).filter(config => config.pixelId && config.accessToken);
    },

    /**
     * Send event to Facebook
     */
    async sendEvent(eventName, pixelConfigs, userData = {}, customData = {}) {
      if (!pixelConfigs || pixelConfigs.length === 0) return;

      for (const config of pixelConfigs) {
        try {
          const eventData = {
            data: [{
              event_name: eventName,
              event_time: Math.floor(Date.now() / 1000),
              action_source: 'website',
              user_data: {
                client_ip_address: userData.ip || '',
                client_user_agent: navigator.userAgent,
                em: userData.email || undefined
              },
              custom_data: {
                currency: 'EUR',
                value: parseFloat((customData.value || 0).toFixed(2)),
                content_type: 'product',
                ...customData
              }
            }]
          };

          await fetch(`https://graph.facebook.com/v12.0/${config.pixelId}/events?access_token=${config.accessToken}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(eventData)
          });
        } catch (error) {
          console.error('Facebook Pixel error:', error);
        }
      }
    },

    /**
     * Store pixels for later use
     */
    storePixels(pixelString) {
      if (pixelString) {
        sessionStorage.setItem('fb_pixels', pixelString);
      }
    },

    /**
     * Get stored pixels
     */
    getStoredPixels() {
      const pixelString = sessionStorage.getItem('fb_pixels');
      return this.parsePixels(pixelString);
    }
  };

  // Cart Manager
  const CartManager = {
    /**
     * Get cart attributes
     */
    async getCartAttributes() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.attributes || {};
      } catch (error) {
        console.error('Error fetching cart attributes:', error);
        return {};
      }
    },

    /**
     * Update cart attributes
     */
    async updateCartAttributes(attributes) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ attributes })
        });
        return await response.json();
      } catch (error) {
        console.error('Error updating cart attributes:', error);
        throw error;
      }
    },

    /**
     * Add item to cart
     */
    async addToCart(variantId, quantity = 1, properties = {}) {
      try {
        // X-FS-No-Track : ajouts techniques (abonnement, page upsell) -> pas d'AddToCart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-FS-No-Track': '1'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: properties
          })
        });
        return await response.json();
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
      }
    },

    /**
     * Remove item from cart
     */
    async removeFromCart(lineKey) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0
          })
        });
        return await response.json();
      } catch (error) {
        console.error('Error removing from cart:', error);
        throw error;
      }
    },

    /**
     * Check if subscription is in cart
     */
    async hasSubscriptionInCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.items.some(item => item.variant_id == CONFIG.subscriptionVariantId);
      } catch (error) {
        console.error('Error checking cart:', error);
        return false;
      }
    },

    /**
     * Remove subscription from cart if present
     */
    async removeSubscriptionFromCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const subItem = cart.items.find(item => item.variant_id == CONFIG.subscriptionVariantId);
        
        if (subItem) {
          await this.removeFromCart(subItem.key);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error removing subscription:', error);
        return false;
      }
    }
  };

  // API Manager
  const API = {
    /**
     * Get client IP address
     */
    async getClientIp() {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip || '';
      } catch (error) {
        console.error('Error fetching client IP:', error);
        return '';
      }
    },

    /**
     * Cancel subscription
     */
    async cancelSubscription(email) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/stop_abo.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Check subscription status
     */
    async checkSubscription(email) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/check_abo_statut.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Validate customer information
     */
    async validateCustomerInfo(customerData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/infos_client.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...customerData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Get customer account info
     */
    async getCustomerAccount(email, orderId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/mon_compte.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          orderId: orderId,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Manage subscription (cancel/activate)
     */
    async manageSubscription(email, action, purchaseId, customerId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/modif_abo_statut.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          action: action,
          purchaseId: purchaseId,
          customerId: customerId,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Generate Youscribe code
     */
    async generateYouscribeCode(email, customerId, orderId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/creer_code_yous.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          domaine: CONFIG.shopDomain,
          customerId: customerId || undefined,
          orderId: orderId || undefined,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Get abandoned cart products
     */
    async getAbandonedCartProducts(productsParam, campaignId) {
      // Parse products string "2593:1;2597:1" → [{id: "2593", quantity: 1}, ...]
      const productsArray = [];
      if (productsParam && typeof productsParam === 'string') {
        const productPairs = productsParam.split(';');
        productPairs.forEach(pair => {
          const [id, qty] = pair.split(':');
          if (id && qty) {
            productsArray.push({
              id: id.trim(),
              quantity: parseInt(qty.trim(), 10) || 1
            });
          }
        });
      }

      const response = await fetch(`${CONFIG.apiBaseUrl}/relance_panier.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaignId || CONFIG.campaignId,
          products: productsArray,
          shopify_locale: CONFIG.locale
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    },

    /**
     * Send partial order (pre-checkout)
     */
    async sendPartial(orderData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/paiement_en_attente.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...orderData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Send order (complete checkout)
     */
    async sendOrder(orderData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/paiement.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...orderData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    }
  };

  // Expose globally for use in page templates
  window.CheckoutChampIntegration = {
    Config: CONFIG,
    Utils: Utils,
    AccountStorage: AccountStorage,
    PriceManager: PriceManager,
    FacebookPixel: FacebookPixel,
    CartManager: CartManager,
    API: API
  };

  // Initialize price manager on page load
  PriceManager.init();

  // Bundle Price Sync - Syncs bundle container prices from main product price
  // This ensures bundle prices always match the main price which updates correctly via Shopify
  (function syncBundlePrices() {
    'use strict';

    // Debounce function to prevent excessive calls
    let syncTimeout = null;
    const debouncedSync = (callback, delay = 50) => {
      if (syncTimeout) clearTimeout(syncTimeout);
      syncTimeout = setTimeout(callback, delay);
    };

    const syncPrice = (productId) => {
      if (!productId) return;

      // Get hidden price element (Shopify updates this on variant change)
      const mainPrice = document.querySelector(
        `.product-price[data-product-id="${productId}"] [data-price-current]`
      );
      const mainOldPrice = document.querySelector(
        `.product-price[data-product-id="${productId}"] [data-price-old]`
      );
      
      const bundlePrice = document.querySelector(
        `.kaching-bundles__bar-pricing[data-product-id="${productId}"] [data-bundle-price]`
      );
      const bundleFullPrice = document.querySelector(
        `.kaching-bundles__bar-pricing[data-product-id="${productId}"] [data-bundle-price-full]`
      );
      
      if (mainPrice && bundlePrice) {
        bundlePrice.textContent = mainPrice.textContent.trim();
      }
      
      if (bundleFullPrice) {
        if (mainOldPrice && mainOldPrice.textContent.trim() && mainOldPrice.style.display !== 'none') {
          bundleFullPrice.textContent = mainOldPrice.textContent.trim();
          bundleFullPrice.style.display = '';
        } else {
          bundleFullPrice.style.display = 'none';
        }
      }
    };

    // Sync all bundle prices on the page
    const syncAllPrices = () => {
      const bundlePricingElements = document.querySelectorAll('.kaching-bundles__bar-pricing[data-product-id]');
      bundlePricingElements.forEach(el => {
        const productId = el.getAttribute('data-product-id');
        syncPrice(productId);
      });
    };

    // Watch main price elements for changes
    const priceObservers = new Map();
    
    const watchMainPrices = () => {
      const mainPriceElements = document.querySelectorAll('.product-price[data-product-id] [data-price-current]');
      
      mainPriceElements.forEach((mainPriceEl) => {
        const priceContainer = mainPriceEl.closest('.product-price');
        const productId = priceContainer?.getAttribute('data-product-id');
        
        if (productId && !priceObservers.has(productId)) {
          const priceObserver = new MutationObserver(() => {
            debouncedSync(() => {
              syncPrice(productId);
            }, 50);
          });
          
          priceObserver.observe(mainPriceEl, { 
            childList: true, 
            subtree: true, 
            characterData: true 
          });
          
          priceObservers.set(productId, priceObserver);
        }
      });
    };

    // Watch for section re-renders (AJAX updates)
    const watchForSectionUpdates = () => {
      const mainContent = document.querySelector('main') || document.querySelector('[role="main"]') || document.body;
      if (!mainContent) return;

      const sectionObserver = new MutationObserver((mutations) => {
        let shouldSync = false;
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) {
                // Check if variant picker or price elements were added/updated
                if (node.matches?.('variant-picker') || 
                    node.querySelector?.('variant-picker') ||
                    node.matches?.('.product-price[data-product-id]') ||
                    node.querySelector?.('.product-price[data-product-id]') ||
                    node.matches?.('.kaching-bundles__bar-pricing') ||
                    node.querySelector?.('.kaching-bundles__bar-pricing')) {
                  // Skip if it's in the cart drawer
                  if (!node.closest?.('#CartDrawer') && !node.closest?.('.cart-drawer')) {
                    shouldSync = true;
                  }
                }
              }
            });
          }
        });

        if (shouldSync) {
          debouncedSync(() => {
            syncAllPrices();
            watchMainPrices();
            watchVariantPickerInputs();
          }, 300);
        }
      });

      sectionObserver.observe(mainContent, {
        childList: true,
        subtree: true
      });
    };

    // Track which inputs we've already added listeners to
    const watchedInputs = new WeakSet();
    
    // Watch variant picker inputs directly
    const watchVariantPickerInputs = () => {
      const inputs = document.querySelectorAll('variant-picker input[type="radio"]');
      
      inputs.forEach((input) => {
        if (!watchedInputs.has(input)) {
          watchedInputs.add(input);
          const variantPicker = input.closest('variant-picker');
          const productId = variantPicker?.getAttribute('data-product-id');
          
          input.addEventListener('change', () => {
            if (productId) {
              // Wait for Shopify to update the hidden price element
              setTimeout(() => {
                syncPrice(productId);
              }, 300);
            }
          });
        }
      });
    };

    // Listen for Shopify custom events
    const setupShopifyEventListeners = () => {
      const handleVariantChange = () => {
        setTimeout(() => {
          syncAllPrices();
          watchMainPrices();
        }, 300);
      };

      document.addEventListener('shopify:variant:change', handleVariantChange);
      document.addEventListener('variant:change', handleVariantChange);

      // Listen for section loads (AJAX section re-renders)
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          syncAllPrices();
          watchMainPrices();
          watchVariantPickerInputs();
        }, 300);
      });
    };

    // Initial sync on page load
    const initialSync = () => {
      syncAllPrices();
      watchMainPrices();
      watchVariantPickerInputs();
      watchForSectionUpdates();
      setupShopifyEventListeners();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialSync);
    } else {
      initialSync();
    }
  })();

})();

/**
 * Cancel Subscription Forms Handler
 * Manages all cancel subscription forms on the page (mobile and desktop)
 */
(function() {
  'use strict';

  if (window.CancelSubscriptionFormsInitialized) return;

  let isProcessing = false;

  function initCancelSubscriptionForms() {
    const forms = document.querySelectorAll('[data-cancel-subscription-form]');
    if (!forms.length) return;

    forms.forEach(function(form) {
      const messageDiv = form.querySelector('.cancel-subscription-message');
      const emailInput = form.querySelector('input[type="email"]');
      const submitBtn = form.querySelector('button[type="submit"]');

      if (!messageDiv || !emailInput || !submitBtn) return;

      form.addEventListener('submit', async function(e) {
        e.preventDefault();
        
        if (isProcessing) return;
        
        const email = emailInput.value.trim();
        if (!email) return;

        if (!window.CheckoutChampIntegration?.API) {
          messageDiv.className = 'cancel-subscription-message error';
          messageDiv.textContent = "Erreur réseau. Veuillez réessayer.";
          messageDiv.style.display = 'block';
          return;
        }

        isProcessing = true;
        submitBtn.disabled = true;
        messageDiv.style.display = 'none';

        try {
          const isLoggedIn = window.CheckoutChampIntegration.AccountStorage.isLoggedIn();
          const accountData = window.CheckoutChampIntegration.AccountStorage.get();

          const result = await window.CheckoutChampIntegration.API.cancelSubscription(email);

          if (result.success) {
            messageDiv.className = 'cancel-subscription-message success';
            messageDiv.textContent = result.message || 'Votre abonnement a été annulé avec succès.';
            messageDiv.style.display = 'block';
            emailInput.value = '';

            if (isLoggedIn && accountData?.customer?.emailAddress === email) {
              try {
                const refreshedData = await window.CheckoutChampIntegration.API.getCustomerAccount(
                  email,
                  accountData.orders?.[0]?.orderId || ''
                );
                
                if (refreshedData.customer) {
                  window.CheckoutChampIntegration.AccountStorage.save(refreshedData);
                  window.dispatchEvent(new CustomEvent('customerAccountUpdated', { detail: refreshedData }));
                }
              } catch (refreshError) {
                console.error('Error refreshing account:', refreshError);
              }
            }
          } else {
            messageDiv.className = 'cancel-subscription-message error';
            messageDiv.textContent = result.message || "Une erreur s\u0026#39;est produite lors de l\u0026#39;annulation de l\u0026#39;abonnement.";
            messageDiv.style.display = 'block';
          }
        } catch (error) {
          console.error('Cancel subscription error:', error);
          messageDiv.className = 'cancel-subscription-message error';
          messageDiv.textContent = "Erreur réseau. Veuillez réessayer.";
          messageDiv.style.display = 'block';
        } finally {
          isProcessing = false;
          submitBtn.disabled = false;
        }
      });
    });
    
    window.CancelSubscriptionFormsInitialized = true;
  }

  // Wait for CheckoutChampIntegration to be available
  const checkInterval = setInterval(function() {
    if (window.CheckoutChampIntegration?.API) {
      clearInterval(checkInterval);
      initCancelSubscriptionForms();
    }
  }, 100);

  // Stop checking after 5 seconds
  setTimeout(function() { clearInterval(checkInterval); }, 5000);
})();


/**
 * Facebook AddToCart Event
 * Utilise window.pixelConfigs et les utilitaires du thème
 */
async function FacebookAddToCart() {
  const productMetaData = await getProductMetaData();
  
  if (productMetaData && productMetaData.meta_product_id_array) {
  
  let productIDs = [];
  let totalPrice = 0.00;
  let contentNames = [];
  let contentCategories = [];
  productMetaData.meta_product_id_array.forEach((item) => {
    let productId = item.id.includes(".") ? item.id.split(".")[0] : item.id;
    let productPrice = item.is_subscription ? 0.00 : item.price / 100;
    productIDs.push(productId);
    contentNames.push(item.product_title || '');
    contentCategories.push(item.product_type || '');
    totalPrice += parseFloat(productPrice);
  });
  
  if (productIDs.length === 0) return;
  
  if (!Array.isArray(window.pixelConfigs) || window.pixelConfigs.length === 0) {
    console.warn("pixelConfigs non défini ou vide");
    return;
  }
  
  const clientIP = await getClientIpAddress();
  
  window.pixelConfigs.forEach(({ pixelId, accessToken }) => {
    fetch(`https://graph.facebook.com/v18.0/${pixelId}/events?access_token=${accessToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        data: [{
          event_name: "AddToCart",
          event_time: Math.floor(new Date().getTime() / 1000),
          action_source: "website",
          user_data: {
            client_ip_address: clientIP,
            client_user_agent: navigator.userAgent
          },
          custom_data: {
            currency: "EUR",
            value: parseFloat(totalPrice.toFixed(2)),
            content_ids: productIDs,
            content_type: "product",
            content_name: contentNames.join(', '),
            content_category: contentCategories.join(', '),
            num_items: productIDs.length
          }
        }]
      })
    })
    .then(response => response.json())
    .then(data => console.log("Facebook AddToCart Response:", data))
    .catch(error => console.error("Facebook AddToCart API Error:", error));
  });
  }
}

// AddToCart : envoyé depuis main-preloaded.js (interception de /cart/add, tous les boutons y compris la sticky bar)
//merge
